import { useEffect, useState } from "react";
import { useBannerStore } from "../../../hooks/useBannerStore";
import { BannerCreateEdit } from "../../../types/slices/bannersType";
import { FormLabel, Grid, MenuItem, TextField } from "@mui/material";
import { Modal } from "../../../components/common/Modal/Modal";
import { ModalHeader } from "../../../components/common/Modal/ModalHeader";
import { ModalBody } from "../../../components/common/Modal/ModalBody";
import { ModalFooter } from "../../../components/common/Modal/ModalFooter";
import { Formik } from "formik";

const today = new Date(
  new Date().toLocaleString("en-US", { timeZone: "America/Lima" }),
)
  .toISOString()
  .split("T")[0];

const yesterday = (() => {
  const peruNow = new Date(
    new Date().toLocaleString("en-US", { timeZone: "America/Lima" }),
  );
  peruNow.setDate(peruNow.getDate() - 1);
  return peruNow.toISOString().split("T")[0];
})();

const MAX_SIZE_MB = 5;

export const CreateEditBannerModal: React.FC<any> = (): JSX.Element | any => {
  const {
    editBanner,
    openModal,
    setOpenModal,
    isEdit,
    createBannerStore,
    editBannerStore,
    getBanners,
  } = useBannerStore();
  const [data, setData] = useState<BannerCreateEdit>({
    title: "",
    description: "",
    image: "",
    order: 1,
    cta_text: "",
    cta_url: "",
    start_date: yesterday,
    end_date: "",
    status: "1",
  });

  const validateImage = (file: File): Promise<string | null> => {
    const allowedTypes = ["image/jpeg", "image/png", "image/jpg", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      return Promise.resolve("Formato inválido. Solo JPG, PNG, JPEG o WEBP");
    }
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      return Promise.resolve("La imagen no debe superar 5MB");
    }
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          if (img.width <= img.height) {
            resolve("La imagen debe ser horizontal (formato banner)");
          } else {
            resolve(null);
          }
        };
        img.onerror = () => {
          resolve("No se pudo validar la imagen");
        };
        img.src = reader.result as string;
      };
      reader.onerror = () => {
        resolve("No se pudo leer el archivo de imagen");
      };
      reader.readAsDataURL(file);
    });
  };

  const validateForm = (values) => {
    let errors: any = {};
    if (!values.title) errors.title = "Titulo es requerido";
    if (values.title && values.title.length > 200)
      errors.title = "El número máximo de caracteres es 200";
    if (!values.order) errors.order = "El orden es requerido";
    if (values.order < 1) errors.order = "El orden debe ser mayor o igual a 1";
    if (!isEdit && !values.image) {
      errors.image = "La imagen es obligatoria";
    }
    return errors;
  };

  const onSubmit = async (values) => {
    const method = isEdit
      ? editBannerStore(editBanner?.id || 0, values)
      : createBannerStore(values);
    const response = await method;
    if (response === true) {
      getBanners();
      setOpenModal(false);
    }
  };

  const clear = () => {
    setData({
      title: "",
      description: "",
      image: "",
      order: 1,
      cta_text: "",
      cta_url: "",
      start_date: yesterday,
      end_date: "",
      status: "1",
    });
  };

  useEffect(() => {
    if (isEdit && openModal) {
      setData(editBanner);
    } else {
      setData({
        title: "",
        description: "",
        image: "",
        order: 1,
        cta_text: "",
        cta_url: "",
        start_date: yesterday,
        end_date: "",
        status: "1",
      });
    }
  }, [isEdit, editBanner, openModal]);

  return (
    <>
      <Modal
        open={openModal}
        handleClose={() => {
          clear();
          setOpenModal(false);
        }}
        disableEscapeKeyDown
        disableBackdropClick
        size="sm"
      >
        <ModalHeader
          text={isEdit ? "Editar Portada" : "Crear Portada"}
          className="positionElements"
          onCancel={() => {
            clear();
            setOpenModal(false);
          }}
        ></ModalHeader>

        <Formik
          initialValues={data}
          enableReinitialize
          validate={(values) => validateForm(values)}
          onSubmit={onSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleSubmit,
            handleChange,
            setFieldValue,
            setFieldError,
          }) => {
            const preview = values.image
              ? typeof values.image === "string"
                ? values.image
                : URL.createObjectURL(values.image)
              : null;
            return (
              <>
                <ModalBody>
                  <form onSubmit={handleSubmit}>
                    <Grid container>
                      <Grid item container xs={12} spacing={2} sx={{ mt: 2 }}>
                        <Grid item xs={12}>
                          <FormLabel>Titulo:</FormLabel>
                          <TextField
                            id="title"
                            type="text"
                            name="title"
                            fullWidth
                            size="small"
                            value={values.title}
                            onChange={handleChange}
                            error={errors.title && touched.title ? true : false}
                            helperText={
                              errors.title && touched.title ? errors.title : ""
                            }
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormLabel>Descripción:</FormLabel>
                          <TextField
                            id="description"
                            name="description"
                            type="text"
                            fullWidth
                            size="small"
                            value={values.description}
                            onChange={handleChange}
                            error={
                              errors.description && touched.description
                                ? true
                                : false
                            }
                            helperText={
                              errors.description && touched.description
                                ? errors.description
                                : ""
                            }
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormLabel>Imagen:</FormLabel>
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={8}>
                              <TextField
                                id="image"
                                name="image"
                                type="file"
                                fullWidth
                                size="small"
                                inputProps={{
                                  accept: "image/png, image/jpeg, image/jpg",
                                }}
                                onChange={async (
                                  event: React.ChangeEvent<HTMLInputElement>,
                                ) => {
                                  const file = event.target.files?.[0];
                                  if (!file) return;
                                  const error = await validateImage(file);
                                  if (error) {
                                    setFieldError("image", error);
                                    return;
                                  }
                                  setFieldValue("image", file);
                                }}
                                error={Boolean(errors.image && touched.image)}
                                helperText={
                                  errors.image && touched.image
                                    ? errors.image
                                    : ""
                                }
                              />
                            </Grid>
                            <Grid item xs={4}>
                              {preview ? (
                                <img
                                  src={preview}
                                  alt="Preview"
                                  loading="lazy"
                                  style={{
                                    width: "100%",
                                    height: 120,
                                    objectFit: "cover",
                                    border: "1px solid #ddd",
                                    borderRadius: 4,
                                    backgroundColor: "#f5f5f5",
                                  }}
                                />
                              ) : (
                                <div
                                  style={{
                                    width: "100%",
                                    height: 120,
                                    border: "1px dashed #ccc",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "#999",
                                    fontSize: 12,
                                  }}
                                >
                                  Sin imagen
                                </div>
                              )}
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item xs={12}>
                          <FormLabel>Orden:</FormLabel>
                          <TextField
                            id="order"
                            name="order"
                            type="number"
                            fullWidth
                            size="small"
                            inputProps={{ min: 1 }}
                            value={values.order || 1}
                            onChange={handleChange}
                            error={errors.order && touched.order ? true : false}
                            helperText={
                              errors.order && touched.order ? errors.order : ""
                            }
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormLabel>Texto de enlace:</FormLabel>
                          <TextField
                            id="cta_text"
                            name="cta_text"
                            type="text"
                            fullWidth
                            size="small"
                            value={values.cta_text}
                            onChange={handleChange}
                            error={
                              errors.cta_text && touched.cta_text ? true : false
                            }
                            helperText={
                              errors.cta_text && touched.cta_text
                                ? errors.cta_text
                                : ""
                            }
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormLabel>URL de enlace:</FormLabel>
                          <TextField
                            id="cta_url"
                            name="cta_url"
                            type="text"
                            fullWidth
                            size="small"
                            value={values.cta_url}
                            onChange={handleChange}
                            error={
                              errors.cta_url && touched.cta_url ? true : false
                            }
                            helperText={
                              errors.cta_url && touched.cta_url
                                ? errors.cta_url
                                : ""
                            }
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormLabel>Fecha de inicio:</FormLabel>
                          <TextField
                            id="start_date"
                            name="start_date"
                            type="date"
                            fullWidth
                            size="small"
                            value={values.start_date}
                            onChange={handleChange}
                            error={
                              errors.start_date && touched.start_date
                                ? true
                                : false
                            }
                            helperText={
                              errors.start_date && touched.start_date
                                ? errors.start_date
                                : ""
                            }
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormLabel>Fecha de fin:</FormLabel>
                          <TextField
                            id="end_date"
                            name="end_date"
                            type="date"
                            fullWidth
                            size="small"
                            value={values.end_date}
                            onChange={handleChange}
                            error={
                              errors.end_date && touched.end_date ? true : false
                            }
                            helperText={
                              errors.end_date && touched.end_date
                                ? errors.end_date
                                : ""
                            }
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormLabel>Estado:</FormLabel>
                          <TextField
                            select
                            id="status"
                            name="status"
                            fullWidth
                            size="small"
                            value={values.status}
                            onChange={handleChange}
                            error={
                              errors.status && touched.status ? true : false
                            }
                            helperText={
                              errors.status && touched.status
                                ? errors.status
                                : ""
                            }
                          >
                            <MenuItem value="1">Activo</MenuItem>
                            <MenuItem value="0">Inactivo</MenuItem>
                          </TextField>
                        </Grid>
                      </Grid>
                    </Grid>
                  </form>
                </ModalBody>
                <ModalFooter
                  confirmText={isEdit ? "Actualizar" : "Registrar"}
                  onConfirm={handleSubmit}
                  cancelText={"Cancelar"}
                  onCancel={() => {
                    setOpenModal(false);
                  }}
                  className="modal-confirm-footer"
                />
              </>
            );
          }}
        </Formik>
      </Modal>
    </>
  );
};
